import { useCallback, useEffect, useState } from 'react';

// Outseta is loaded via <script> in public/index.html and exposes window.Outseta.
// This hook mirrors Outseta's login state into React so routes and the header can react to it.

const OUTSETA_LOGIN_URL = 'https://ebta.outseta.com/auth?widgetMode=login';
const POLL_INTERVAL_MS = 100;
const POLL_TIMEOUT_MS = 5000;

const ANONYMOUS = { status: 'anonymous', user: null };

function readAuthState(outseta) {
  const token = outseta.getAccessToken();
  if (!token) return ANONYMOUS;

  const payload = outseta.getJwtPayload() || {};
  const expiresAtMs = payload.exp ? payload.exp * 1000 : null;
  if (expiresAtMs && expiresAtMs < Date.now()) return ANONYMOUS;

  return { status: 'authenticated', user: payload };
}

export default function useOutsetaAuth() {
  const [state, setState] = useState({ status: 'loading', user: null });

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const startedAt = Date.now();

    const attach = (outseta) => {
      setState(readAuthState(outseta));
      outseta.on('accessToken.set', () => {
        if (!cancelled) setState(readAuthState(outseta));
      });
      outseta.on('logout', () => {
        if (!cancelled) setState(ANONYMOUS);
      });
    };

    const waitForScript = () => {
      if (cancelled) return;
      if (window.Outseta) {
        attach(window.Outseta);
        return;
      }
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        // Script blocked or failed to load: behave as logged out.
        setState(ANONYMOUS);
        return;
      }
      timer = setTimeout(waitForScript, POLL_INTERVAL_MS);
    };

    waitForScript();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const login = useCallback(() => {
    if (window.Outseta && window.Outseta.auth) {
      window.Outseta.auth.open({
        widgetMode: 'login',
        mode: 'popup',
        authenticationCallbackUrl: window.location.href,
      });
      return;
    }
    // Fallback if the script did not load: go to Outseta's hosted login page.
    window.open(OUTSETA_LOGIN_URL, '_blank', 'noopener');
  }, []);

  const openProfile = useCallback(() => {
    if (window.Outseta && window.Outseta.profile) {
      window.Outseta.profile.open({ tab: 'profile' });
    }
  }, []);

  return {
    status: state.status,
    user: state.user,
    isAuthenticated: state.status === 'authenticated',
    login,
    openProfile,
  };
}
